import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import XionLogo from "../components/XionLogo";
import PasswordInput from "../components/PasswordInput";
import { api, formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F8F6] flex items-center justify-center px-4">
        <div className="glass-card rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h1 className="font-display font-black text-2xl text-[#0E0E1A] mb-2">Invalid link</h1>
          <p className="text-[#5A5A72] mb-4">This reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" className="text-[#2060EE] hover:underline text-sm">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F6] flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-10">
          <XionLogo size={32} withText />
        </Link>

        {done ? (
          <div className="glass-card rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#2060EE] mb-4" />
            <h1 className="font-display font-black text-2xl text-[#0E0E1A] mb-2">Password updated!</h1>
            <p className="text-[#5A5A72]">Redirecting you to sign in...</p>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">/ set new password</p>
            <h1 className="font-display font-black text-4xl text-[#0E0E1A] mt-2 mb-2">New password.</h1>
            <p className="text-[#5A5A72] mb-8">Choose a strong password of at least 8 characters.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">New Password</label>
                <div className="mt-2">
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="reset-password-input" minLength={8} autoComplete="new-password" placeholder="Min. 8 characters" />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Confirm Password</label>
                <div className="mt-2">
                  <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="reset-confirm-input" minLength={8} autoComplete="new-password" placeholder="Repeat password" />
                </div>
              </div>

              {error && (
                <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full btn-primary py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Updating..." : <>Update password <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
